/**
 * Message text for queued reminders.
 *
 * ⚠️ These templates are duplicated in supabase/functions/send-notifications/
 * index.ts (Deno). That function is undeployed while Twilio is parked, but it
 * remains the path a server-side dispatch would take, so the wording must stay
 * in step. Change both together.
 *
 * Every field is optional in practice: the payload is built by a SQL trigger
 * from rows that may have nulls (a task with no room, a contact with no first
 * name). The templates degrade rather than printing "undefined".
 */

import type { NotificationKind } from '@/lib/data/notifications';

export interface ReminderPayload {
  /**
   * The exact message the app sent, when the app composed it (0044). Wins over
   * every template below: a punch list is a list of specific items, and
   * rebuilding it later from data that has since changed would resend a
   * DIFFERENT message under the same record.
   */
  text?: string | null;
  to_phone?: string | null;
  first_name?: string | null;
  project_name?: string | null;
  room_name?: string | null;
  task?: string | null;
  date_label?: string | null;
  portal_url?: string | null;
}

export interface ReminderSource {
  kind: NotificationKind;
  payload: ReminderPayload | null;
}

/** The message body, without the portal URL — that rides along separately. */
export function buildReminderMessage(n: ReminderSource): string {
  const p = n.payload ?? {};
  // A message the APP composed and sent carries its own text (0044): a punch
  // list is a list, and re-deriving it later would send a different message
  // from the one that went out.
  const stored = clean((p as { text?: string | null }).text);
  if (stored) return stored;
  const name = clean(p.first_name) ?? 'there';
  const task = clean(p.task) ?? 'scheduled work';
  const project = clean(p.project_name) ?? 'your project';
  const date = clean(p.date_label);
  const room = clean(p.room_name);
  const where = room ? ` (${room})` : '';
  // "on Tuesday, March 3" reads fine; a missing date must not leave "for  ."
  const when = date ? ` ${date}` : '';

  switch (n.kind) {
    case 'contractor_t_minus_7':
      return `Hi ${name} — heads up: ${task}${where} for ${project} is scheduled for${when || ' an upcoming date'} (one week out).`;
    case 'contractor_t_minus_2':
      return `Hi ${name} — reminder: ${task}${where} for ${project} starts${when || ' soon'} (in a couple of days).`;
    case 'contractor_t_zero':
      return `Hi ${name} — today's the day: ${task}${where} for ${project}.`;
    case 'change_order_new':
      return `Hi ${name} — there's a new change order for ${project}.`;
    case 'change_order_acknowledged':
      return `Heads up — the client just acknowledged a change order for ${project}.`;
    case 'punch_list':
      // Only reached when the stored text is missing, which means the row was
      // written by something that didn't save it.
      return `Hi ${name} — a punch list for ${project}.`;
    case 'note':
      // Same: a sent note always stores its own text (payload.text wins over
      // every template, 0044), so this is the row-written-wrong fallback.
      return `Hi ${name} — an update on ${project}.`;
    default:
      return `Update from ${project}: ${task}${where}${when ? ' on' + when : ''}.`;
  }
}

export interface PortalLinkContext {
  /**
   * Where this firm's portal links live — `portalLinkBase()` from
   * @/lib/current-firm, which is the firm's own address when it has one.
   */
  base: string;
  /**
   * The contractor's CURRENT portal link, when the caller knows it. Preferred
   * over whatever the reminder stored: a reminder queued before the link was
   * rotated carries the old token, and that link is dead.
   */
  liveUrl?: string | null;
}

/**
 * The link a reminder should carry — absolute, and clickable in a text message.
 *
 * `payload.portal_url` is written by a SQL trigger (0013/0018) which reads a
 * `app.public_url` setting that nobody sets, and COULD not set correctly
 * anyway: this firm answers on two addresses, and the right one depends on the
 * firm, not the database. So the trigger falls through to a bare
 * `#/contractor/<token>` — which is exactly what landed in Mick's Messages app
 * on 2026-09-30, a fragment with no host in front of it and nothing for the
 * contractor to tap.
 *
 * The host is therefore decided HERE, in the browser, where it is known. Same
 * rule as an invite email, which returns to the address the sender is using.
 */
export function reminderUrl(n: ReminderSource, ctx?: PortalLinkContext): string | null {
  const live = clean(ctx?.liveUrl);
  if (live) return live;
  const stored = clean(n.payload?.portal_url);
  if (!stored) return null;
  if (/^https?:\/\//i.test(stored)) return stored;
  if (!ctx?.base) return null;   // a relative fragment alone is worse than none
  return absolutize(stored, ctx.base);
}

/** `#/contractor/abc` + `https://firm.example/` → `https://firm.example/#/contractor/abc`. */
function absolutize(stored: string, base: string): string {
  try {
    return new URL(stored, base).toString();
  } catch {
    // A base the URL parser won't take. Joining by hand beats dropping the
    // link, and the only shapes reaching here are a host with or without a
    // trailing slash.
    return stored.startsWith('#') && !base.endsWith('/')
      ? `${base}/${stored}`
      : `${base}${stored}`;
  }
}

/** Recipient phone, or null. */
export function reminderPhone(n: ReminderSource): string | null {
  return clean(n.payload?.to_phone) ?? null;
}

/**
 * Message accompanying a contractor's portal link.
 *
 * Lives here with the other templates rather than inline in the contact card:
 * it is the same kind of thing (wording sent on the designer's behalf) and the
 * same degradation rules apply when a name is missing.
 */
export function buildPortalInviteMessage(
  contactName: string | null | undefined,
  projectName: string | null | undefined,
): string {
  const first = clean(contactName)?.split(/\s+/)[0] ?? 'there';
  const project = clean(projectName);
  const forProject = project ? ` for ${project}` : '';
  return `Hi ${first} — here's your portal${forProject}. `
    + `It has your schedule and the materials for your work.`;
}

/** Short label for the reminder list. */
export function reminderKindLabel(kind: NotificationKind): string {
  switch (kind) {
    case 'contractor_t_minus_7': return 'One week out';
    case 'contractor_t_minus_2': return 'Two days out';
    case 'contractor_t_zero': return 'Day of';
    case 'change_order_new': return 'New change order';
    case 'change_order_acknowledged': return 'Change order acknowledged';
    case 'punch_list': return 'Punch list';
    case 'note': return 'Note';
    default: return 'Reminder';
  }
}

/**
 * Whether an unsent reminder is stale enough to drop off the list.
 *
 * Three days past its send date (the designer's choice). A reminder for work
 * that already started is noise, and a to-do list with noise in it stops being
 * read at all — which costs more than the occasional missed nudge.
 */
export const STALE_AFTER_DAYS = 3;

export function isStaleReminder(
  scheduledFor: string,
  now: Date = new Date(),
): boolean {
  const ts = new Date(scheduledFor).getTime();
  if (Number.isNaN(ts)) return false;
  return ts < now.getTime() - STALE_AFTER_DAYS * 86_400_000;
}

/** Cutoff an "expire the stale ones" write should use. */
export function staleCutoffIso(now: Date = new Date()): string {
  return new Date(now.getTime() - STALE_AFTER_DAYS * 86_400_000).toISOString();
}

function clean(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

// ─── The history line ───────────────────────────────────────────────────────

/** What a row of the Notifications list needs to describe itself. */
export interface SentRecord {
  status: 'pending' | 'sent' | 'shared' | 'failed' | 'cancelled';
  scheduled_for: string;
  sent_at: string | null;
  /** 0044. Older rows, and rows read before that migration, have none. */
  send_count?: number | null;
}

/**
 * "Sent Sep 22, 3:04 PM · sent 3 times", or what the message is still waiting
 * for.
 *
 * The count only appears above one: "sent 1 time" beside a timestamp says
 * nothing the timestamp didn't, and a list where every row carries it stops
 * being readable at the one place it matters.
 *
 * timestamptz on both fields, so `new Date` is right here — these are moments,
 * not date-only columns (see @/lib/dates).
 */
export function notificationStamp(n: SentRecord, fmt: (iso: string) => string = defaultStamp): string {
  const times = (n.send_count ?? 0) > 1 ? ` · sent ${n.send_count} times` : '';
  if (n.status === 'cancelled') return 'Cancelled';
  if (n.sent_at && (n.status === 'sent' || n.status === 'shared')) return `Sent ${fmt(n.sent_at)}${times}`;
  if (n.status === 'failed') return `Failed${n.sent_at ? ` ${fmt(n.sent_at)}` : ''}`;
  return `Scheduled ${fmt(n.scheduled_for)}`;
}

const defaultStamp = (iso: string): string => new Date(iso).toLocaleString();

/**
 * Whether a message can be sent again from the history.
 *
 * Everything except a cancelled one: a reminder the designer dismissed was
 * dismissed on purpose, and offering to send it would undo that decision
 * without saying so. A pending one can be sent from here too — it is the same
 * message the "Reminders to send" panel offers.
 */
export function canResend(n: Pick<SentRecord, 'status'>): boolean {
  return n.status !== 'cancelled';
}

/** "Send again" once it has gone out at least once, "Send" before that. */
export function resendLabel(n: Pick<SentRecord, 'send_count'>): string {
  return (n.send_count ?? 0) > 0 ? 'Send again' : 'Send';
}
